/**
 * Builds employee-facing scale options from catalogue Resp1…Resp5 labels.
 *
 * Given `k` non-empty labels in Resp order (holes treated as empty and skipped),
 * Resp1 maps to answerID `k`, the next filled label to `k-1`, … last filled → `1`.
 * Matches Slack/Teams packing: `5 - index - emptyAnswers` when empties are trailing.
 */

export type StandardPulseScaleOption = {
  /** Stored answerID as a string (survey form values). */
  value: string;
  label: string;
  /** Numeric answerID used in reporting (`k` … `1`). */
  answerID: number;
};

/**
 * @param responseLabels Ordered Resp1…RespN labels (typically length 5)
 */
export function buildStandardPulseScaleOptions(
  responseLabels: readonly (string | null | undefined)[]
): StandardPulseScaleOption[] {
  const filled = (responseLabels ?? [])
    .map((label) => String(label ?? '').trim())
    .filter((label) => label.length > 0);

  const k = filled.length;
  if (k === 0) {
    return [];
  }

  return filled.map((label, index) => {
    const answerID = k - index;
    return {
      value: String(answerID),
      label,
      answerID,
    };
  });
}

/**
 * answerID shown next to composer option `index` (0-based) for a scale of `maxScore` slots.
 */
export function resolvePulseComposerAnswerID(index: number, maxScore: number): number {
  const safeMax = Number.isFinite(maxScore) && maxScore > 0 ? Math.floor(maxScore) : 5;
  return safeMax - index;
}

/**
 * Clamp company pulse maxScore into the supported Resp slot range (2–5).
 */
export function resolvePulseComposerMaxScore(maxScore: unknown): number {
  const n = typeof maxScore === 'number' ? maxScore : Number(maxScore);
  if (!Number.isFinite(n)) {
    return 5;
  }
  return Math.min(5, Math.max(2, Math.floor(n)));
}
